import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../auth/useAuth';

export function usePosts() {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    fetchPosts();
    
    // Set up real-time subscription for posts
    const postsSubscription = supabase
      .channel('public:posts')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'posts' }, payload => {
        if (payload.eventType === 'INSERT') {
          fetchPost(payload.new.id);
        } else if (payload.eventType === 'DELETE') {
          setPosts(prevPosts => prevPosts.filter(post => post.id !== payload.old.id));
        } else if (payload.eventType === 'UPDATE') {
          fetchPost(payload.new.id);
        }
      })
      .subscribe();
      
    // Set up real-time subscription for comments
    const commentsSubscription = supabase
      .channel('public:post_comments')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'post_comments' }, payload => {
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE' || payload.eventType === 'DELETE') {
          // Refresh the post that this comment belongs to
          if (payload.new && payload.new.post_id) {
            fetchPost(payload.new.post_id);
          } else if (payload.old && payload.old.post_id) {
            fetchPost(payload.old.post_id);
          }
        }
      })
      .subscribe();

    // Clean up subscription
    return () => {
      supabase.removeChannel(postsSubscription);
      supabase.removeChannel(commentsSubscription);
    };
  }, []);

  const fetchPosts = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('posts')
        .select(`
          *,
          user_profiles(*)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Fetch post_comments separately
      if (data && data.length > 0) {
        for (const post of data) {
          const { data: comments, error: commentsError } = await supabase
            .from('post_comments')
            .select(`
              *,
              user_profiles(*)
            `)
            .eq('post_id', post.id)
            .order('created_at', { ascending: true });
            
          if (!commentsError) {
            post.comments = comments || [];
          } else {
            post.comments = [];
          }
        }
      }
      
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPost = async (postId) => {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select(`
          *,
          user_profiles(*)
        `)
        .eq('id', postId)
        .single();

      if (error) throw error;
      
      // Fetch comments separately
      const { data: comments, error: commentsError } = await supabase
        .from('post_comments')
        .select(`
          *,
          user_profiles(*)
        `)
        .eq('post_id', postId)
        .order('created_at', { ascending: true });
        
      if (!commentsError) {
        data.comments = comments || [];
      } else {
        data.comments = [];
      }
      
      // Update the specific post in the state
      setPosts(prevPosts => {
        const existingPostIndex = prevPosts.findIndex(p => p.id === postId);
        if (existingPostIndex >= 0) {
          const updatedPosts = [...prevPosts];
          updatedPosts[existingPostIndex] = data;
          return updatedPosts;
        } else {
          return [data, ...prevPosts];
        }
      });
    } catch (error) {
      console.error('Error fetching post:', error);
    }
  };

  // Generate a simple unique ID (alternative to uuid package)
  const generateUniqueId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  };

  // Create a post with optional media
  const createPost = async (content, mediaFile = null, mediaType = null) => {
    if (!user) throw new Error('You must be logged in to create a post');
    
    try {
      // Create post first without media
      const { data, error } = await supabase
        .from('posts')
        .insert([{
          user_id: user.id,
          content,
          // Initially set media to null
          media_url: null,
          media_type: null
        }])
        .select();
        
      if (error) throw error;
      
      const postId = data[0].id;
      
      // Then try to add media if provided
      if (mediaFile && mediaType) {
        try {
          // If mediaFile is already in base64 format
          if (typeof mediaFile === 'object' && mediaFile.data) {
            // Extract base64 data - remove the prefix (data:image/jpeg;base64,)
            const base64Data = mediaFile.data.split(',')[1];
            
            // Convert base64 to binary
            const binaryData = atob(base64Data);
            
            // Create a Uint8Array
            const uint8Array = new Uint8Array(binaryData.length);
            for (let i = 0; i < binaryData.length; i++) {
              uint8Array[i] = binaryData.charCodeAt(i);
            }
            
            // Create a blob from the Uint8Array
            const blob = new Blob([uint8Array], { type: mediaFile.type });
            
            // Generate a filename
            const fileExt = mediaFile.name.split('.').pop() || 
                          (mediaType === 'image' ? 'jpg' : 'mp4');
            const fileName = `${generateUniqueId()}.${fileExt}`;
            
            // Update post with the media URL
            await updatePostWithMedia(postId, blob, fileName, mediaType);
          }
        } catch (mediaError) {
          console.error('Error uploading media:', mediaError);
          // We already have the post created, just without media
        }
      }
      
      return data[0];
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    }
  };
  
  // Helper function to update a post with media
  const updatePostWithMedia = async (postId, file, fileName, mediaType) => {
    try {
      // Use the exact bucket name from your Supabase dashboard
      const bucket = 'Post media';
      
      // Create the file path with user ID as the first folder
      const filePath = `${user.id}/${fileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });
        
      if (uploadError) {
        console.error(`Upload error:`, uploadError);
        return;
      }
      
      // Generate URL manually instead of using getPublicUrl
      const supabaseUrl = supabase.supabaseUrl;
      const mediaUrl = `${supabaseUrl}/storage/v1/object/public/${bucket}/${filePath}`;
      
      // Update the post with the media URL
      const { error: updateError } = await supabase
        .from('posts')
        .update({
          media_url: mediaUrl,
          media_type: mediaType
        })
        .eq('id', postId);
        
      if (updateError) {
        console.error('Error updating post with media URL:', updateError);
      }
    } catch (error) {
      console.error('Error in updatePostWithMedia:', error);
    }
  };

  const toggleLike = async (postId) => {
    if (!user) return;
    
    try {
      // Check if the user has already liked the post
      const { data: existingLike, error: likeCheckError } = await supabase
        .from('post_likes')
        .select('*')
        .eq('post_id', postId)
        .eq('user_id', user.id)
        .single();
        
      if (likeCheckError && likeCheckError.code !== 'PGRST116') {
        throw likeCheckError;
      }
      
      // Toggle like
      if (existingLike) {
        // Remove like
        const { error: unlikeError } = await supabase
          .from('post_likes')
          .delete()
          .eq('id', existingLike.id);
          
        if (unlikeError) throw unlikeError;
      } else {
        // Add like
        const { error: likeError } = await supabase
          .from('post_likes')
          .insert([{ post_id: postId, user_id: user.id }]);
          
        if (likeError) throw likeError;
      }
      
      // Update post in state to reflect changes
      await fetchPost(postId);
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const addComment = async (postId, content) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('post_comments')
        .insert([{
          post_id: postId,
          user_id: user.id,
          content
        }]);
        
      if (error) throw error;
      
      // Fetch the updated post
      await fetchPost(postId);
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const deletePost = async (postId) => {
    try {
      // First, check if the post has media to delete
      const { data: postData, error: postError } = await supabase
        .from('posts')
        .select('media_url, media_type')
        .eq('id', postId)
        .single();
        
      if (postError) throw postError;
      
      // Delete the post (cascade should handle comments and likes)
      const { error } = await supabase
        .from('posts')
        .delete()
        .eq('id', postId);
        
      if (error) throw error;
      
      // Update local state
      setPosts(prevPosts => prevPosts.filter(post => post.id !== postId));
    } catch (error) {
      console.error('Error deleting post:', error);
      throw error;
    }
  };

  return {
    posts,
    isLoading,
    createPost,
    toggleLike,
    addComment,
    deletePost
  };
}